import { z } from 'zod';
import { createOffsetPaginatedSchema, OffsetPaginationParamsSchema, } from '../offset-pagination.js';
import { PATIENT_VISIT_FLAGS } from '../patients/enrollments.schema.js';
import { TRIAL_VISIT_TYPES } from '../trials/trials.schema.js';
// ---------------------------------------------------------------------------
// Day plan of a trial (Domain 4 — Cockpit, element 1 of the legend)
// ---------------------------------------------------------------------------
/**
 * Kit registration flag of a scheduled visit, as the day plan shows it
 * (REQ-VIS-031: white / red / green). Rides the SAME catalog as the chart's
 * `PatientVisitFlagSchema` — re-exported here (not redefined) so the cockpit
 * and the chart can never drift apart.
 */
export const DayPlanKitFlagSchema = z.enum(Object.values(PATIENT_VISIT_FLAGS));
/**
 * Status of a visit as the plan derives it: `date_real` empty ⇒ planned,
 * `date_real` filled ⇒ registered (REQ-VIS-035). DERIVED, never stored — the
 * day plan adds no "status" column (`patient_visits` has none, by design).
 */
export const DAY_PLAN_STATUSES = {
    PLANNED: 'planned',
    REGISTERED: 'registered',
};
export const DayPlanStatusSchema = z.enum(Object.values(DAY_PLAN_STATUSES));
/**
 * One row of the day plan (REQ-VIS-002, element 1: Godz. / Pacjent / Wizyta /
 * Typ / Status / Flaga kitów).
 *
 * `visitName` is resolved in Domain 4 from the Domain 2 catalog position as the
 * two-part label `visit_name1 (visit_name2)` (mail 6 D.5); when `visit_name2`
 * is absent the name falls back to `visit_name1` alone. The read door in
 * Domain 3 deliberately does not join Domain 2.
 * `visitNumber` is the protocol ordering and drives the visual sort alongside
 * the date.
 *
 * `time` is the scheduled hour of a visit (REQ-VIS-015, HH:MM format, nullable).
 */
export const DayPlanVisitDtoSchema = z.object({
    visitId: z.number().int().positive(),
    enrollmentId: z.number().int().positive(),
    patientId: z.number().int().positive(),
    patientFirstName: z.string().min(1).max(255),
    patientLastName: z.string().min(1).max(255),
    trialVisitId: z.number().int().positive(),
    visitName: z.string().min(1).max(255),
    visitNumber: z.number().int().positive(),
    /** Contact channel (legacy S/P/H/I) — the "Typ" column. */
    type: z.enum(Object.values(TRIAL_VISIT_TYPES)),
    /** Calendar day of the plan (`date`, not an instant). */
    date: z.iso.date(),
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable(),
    status: DayPlanStatusSchema,
    flag: DayPlanKitFlagSchema.nullable(),
    confirmedAt: z.iso.datetime().nullable(),
    mainComment: z.string().nullable(),
    /** REQ-PAT-011: screening number of the enrollment — shown in the patient column and searchable. */
    screeningNumber: z.string().min(1).max(64),
    /** REQ-PAT-012: optional randomization number — shown when present, searchable. */
    randomizationNumber: z.string().max(64).nullable(),
});
/** Offset page of the day plan — envelope shared with `GET /patients`. */
export const DayPlanDtoSchema = createOffsetPaginatedSchema(DayPlanVisitDtoSchema);
/**
 * Query of `GET /api/v1/visits`. `trialId` and `date` are REQUIRED — a plan
 * without a trial or without a day is meaningless (the cockpit always has both
 * picked), so an omitted field is a 400, not an unfiltered dump.
 *
 * Pagination (`page`/`limit`) is inherited from the shared envelope; `limit`
 * is capped at 100 (the default 25 fits a typical day).
 */
export const DayPlanQuerySchema = OffsetPaginationParamsSchema.extend({
    trialId: z.coerce.number().int().positive(),
    date: z.iso.date(),
    /**
     * Optional status filter — restricts the plan to `planned` or `registered`
     * visits. Omitted means "all statuses" (the default view).
     */
    status: DayPlanStatusSchema.optional(),
    /**
     * Free-text search over patient name (first + last) and enrollment numbers
     * (screening + randomization). Case-insensitive substring match. Omitted or
     * blank means "no text filter".
     */
    search: z.string().trim().max(100).optional(),
});
//# sourceMappingURL=day-plan.schema.js.map